import { resolveImageUrl } from "@/utils";
import type { UserInfo } from "#/entity";

export interface SignInReq {
	username: string;
	password: string;
}

export interface LoginPayload {
	user_account: string;
	user_password: string;
}

export interface LoginResponse {
	access_token: string;
	refresh_token: string;
	user?: UserInfo;
}

export interface RefreshPayload {
	refresh_token: string;
}

export interface MyInfoResponse {
	user_id: string;
	user_account: string;
	user_mobile?: string;
	user_nickname?: string;
	nickname?: string;
	user_level: number;
	user_levels?: UserLevelOption[];
	user_img: string;
	user_sign: string;
	share_code: string;
}

export interface UserLevelOption {
	value: number;
	name: string;
}

export interface MyInfo {
	userId: string;
	userAccount: string;
	userMobile: string;
	userNickname: string;
	userLevel: number;
	userLevelName: string;
	userImg: string;
	userImgPath: string;
	userSign: string;
	shareCode: string;
}

export interface MySuperiorResponse {
	user_id?: string;
	user_account: string;
	user_img: string;
	user_mobile: string;
	user_nickname: string;
	user_level: number;
	relation: string;
	user_sign: string;
	create_time: string;
}

export interface MySuperior {
	userId: string;
	account: string;
	phone: string;
	avatar: string;
	signature: string;
}

export interface MyTeamParams {
	page: number;
	pagesize: number;
	account: string;
}

export interface MyTeamItemResponse {
	user_id?: string;
	id?: string;
	user_account?: string;
	account?: string;
	user_mobile?: string;
	mobile?: string;
	user_nickname?: string;
	nickname?: string;
	relation?: string;
	user_level?: number;
	user_level_name?: string;
	create_time?: string;
	created_at?: string;
	user_img?: string;
}

export interface MyTeamResponse {
	total?: number;
	page?: number;
	page_size?: number;
	pagesize?: number;
	items?: MyTeamItemResponse[];
	list?: MyTeamItemResponse[];
	team?: MyTeamItemResponse[];
	teams?: MyTeamItemResponse[];
	data?: MyTeamItemResponse[];
}

export interface MyTeamMember {
	userId: string;
	name: string;
	nickname: string;
	phone: string;
	tag: string;
	userLevel: number | null;
	levelName: string;
	date: string;
	avatar: string;
}

export interface MyTeamData {
	total: number;
	page: number;
	pageSize: number;
	records: MyTeamMember[];
}

export interface SetInfoReq {
	userImg: string;
	userNickname: string;
	userSign: string;
}

export interface SetInfoPayload {
	user_img?: string;
	user_nickname?: string;
	user_sign?: string;
}

export interface PasswordResetForm {
	mobile: string;
	code: string;
	newPassword: string;
	confirmPassword: string;
}

export interface PasswordResetReq {
	mobile: string;
	code: string;
	newPassword: string;
}

export interface PasswordResetPayload {
	user_mobile: string;
	code: string;
	newPassword: string;
}

export type PayPasswordResetForm = PasswordResetForm;
export type PayPasswordResetReq = PasswordResetReq;
export type PayPasswordResetPayload = PasswordResetPayload;

export interface LoginPasswordEditForm {
	oldPassword: string;
	newPassword: string;
	confirmPassword: string;
}

export interface LoginPasswordEditReq {
	oldPassword: string;
	newPassword: string;
}

export interface RegisterForm {
	mobile: string;
	code: string;
	share: string;
	password: string;
	confirmPassword: string;
}

export interface RegisterReq {
	mobile: string;
	code: string;
	share: string;
	password: string;
}

export interface RegisterPayload {
	user_mobile: string;
	smscode: string;
	share: string;
	user_password: string;
}

export const toLoginPayload = (data: SignInReq): LoginPayload => ({
	user_account: data.username,
	user_password: data.password,
});

export const mapLoginResponse = (data: LoginResponse, fallbackUserAccount = "") => ({
	accessToken: data.access_token,
	refreshToken: data.refresh_token,
	user:
		data.user ??
		({
			id: fallbackUserAccount,
			username: fallbackUserAccount,
			email: "",
			permissions: [],
			roles: [],
		} satisfies UserInfo),
});

export const toRefreshPayload = (refreshToken: string): RefreshPayload => ({
	refresh_token: refreshToken,
});

const userLevelNames: Record<number, string> = {
	1: "会员",
	2: "VIP",
	3: "微店",
	4: "县代",
	5: "市代",
	6: "省代",
	7: "大区",
};

export const getUserLevelName = (level: number): string => userLevelNames[level] ?? "未知等级";

const getUserLevelNameFromOptions = (options: UserLevelOption[] | undefined, level: number): string =>
	options?.find((item) => item.value === level)?.name || getUserLevelName(level);

export const mapMyInfoResponse = (data: MyInfoResponse): MyInfo => ({
	userId: data.user_id ?? "",
	userAccount: data.user_account ?? "",
	userMobile: data.user_mobile ?? "",
	userNickname: data.user_nickname ?? data.nickname ?? "",
	userLevel: data.user_level ?? 0,
	userLevelName: getUserLevelNameFromOptions(data.user_levels, data.user_level ?? 0),
	userImg: resolveImageUrl(data.user_img),
	userImgPath: data.user_img ?? "",
	userSign: data.user_sign ?? "",
	shareCode: data.share_code ?? "",
});

export const mapMySuperiorResponse = (data: MySuperiorResponse): MySuperior => ({
	userId: data.user_id ?? "",
	account: data.user_account ?? "",
	phone: data.user_mobile ?? "",
	avatar: resolveImageUrl(data.user_img),
	signature: data.user_sign ?? "",
});

const formatTeamTime = (value: string | undefined): string => {
	if (!value) return "";
	return value.replace(/\.\d+$/, "");
};

const getMyTeamItems = (data: MyTeamResponse | MyTeamItemResponse[] | null | undefined): MyTeamItemResponse[] => {
	if (Array.isArray(data)) return data;
	return data?.items ?? data?.list ?? data?.team ?? data?.teams ?? data?.data ?? [];
};

export const normalizeMyTeamResponse = (data: MyTeamResponse | MyTeamItemResponse[] | null | undefined): MyTeamData => {
	const records = getMyTeamItems(data).map((item) => ({
		userId: item.user_id ?? item.id ?? "",
		name: item.user_account ?? item.account ?? item.user_nickname ?? item.nickname ?? "",
		nickname: item.user_nickname ?? item.nickname ?? "",
		phone: item.user_mobile ?? item.mobile ?? "",
		tag: item.relation ?? item.user_level_name ?? "推荐",
		userLevel: typeof item.user_level === "number" ? item.user_level : null,
		levelName: item.user_level_name || (typeof item.user_level === "number" ? getUserLevelName(item.user_level) : ""),
		date: formatTeamTime(item.create_time ?? item.created_at),
		avatar: resolveImageUrl(item.user_img),
	}));
	return {
		total: Array.isArray(data) ? records.length : (data?.total ?? records.length),
		page: Array.isArray(data) ? 1 : (data?.page ?? 1),
		pageSize: Array.isArray(data) ? records.length : (data?.page_size ?? data?.pagesize ?? records.length),
		records,
	};
};

export const toSetInfoPayload = (data: SetInfoReq): SetInfoPayload => {
	const payload: SetInfoPayload = {};
	if (data.userImg) payload.user_img = data.userImg;
	if (data.userNickname) payload.user_nickname = data.userNickname;
	if (data.userSign) payload.user_sign = data.userSign;
	return payload;
};

export const toPasswordSmsPayload = (mobile: string) => ({
	mobile,
});

export const isValidMobile = (mobile: string) => /^1\d{10}$/.test(mobile.trim());

export const toPasswordResetPayload = (data: PasswordResetReq): PasswordResetPayload => ({
	user_mobile: data.mobile,
	code: data.code,
	newPassword: data.newPassword,
});

export const toPayPasswordResetPayload = (data: PayPasswordResetReq): PayPasswordResetPayload => ({
	user_mobile: data.mobile,
	code: data.code,
	newPassword: data.newPassword,
});

export const validatePasswordStrength = (password: string): string | null => {
	const value = password.trim();
	if (value.length < 8 || value.length > 20 || !/[A-Za-z]/.test(value) || !/\d/.test(value)) {
		return "密码需为8-20位，并包含字母和数字";
	}
	return null;
};

export const validatePasswordResetForm = (data: PasswordResetForm): string | null => {
	if (!data.mobile.trim()) return "请输入手机号码";
	if (!isValidMobile(data.mobile)) return "请输入正确的手机号码";
	if (!data.code.trim()) return "请输入验证码";
	if (!data.newPassword.trim()) return "请输入新密码";
	if (!data.confirmPassword.trim()) return "请确认新密码";
	const passwordError = validatePasswordStrength(data.newPassword);
	if (passwordError) return passwordError;
	if (data.newPassword !== data.confirmPassword) return "两次输入的密码不一致";
	return null;
};

export const validatePayPasswordResetForm = (data: PayPasswordResetForm): string | null => {
	if (!data.mobile.trim()) return "请输入手机号码";
	if (!isValidMobile(data.mobile)) return "请输入正确的手机号码";
	if (!data.code.trim()) return "请输入验证码";
	if (!data.newPassword.trim()) return "请输入新支付密码";
	if (!data.confirmPassword.trim()) return "请确认新支付密码";
	const passwordError = validatePasswordStrength(data.newPassword);
	if (passwordError) return passwordError;
	if (data.newPassword !== data.confirmPassword) return "两次输入的密码不一致";
	return null;
};

export const validateLoginPasswordEditForm = (data: LoginPasswordEditForm): string | null => {
	if (!data.oldPassword.trim()) return "请输入原密码";
	if (!data.newPassword.trim()) return "请输入新密码";
	if (!data.confirmPassword.trim()) return "请确认新密码";
	const passwordError = validatePasswordStrength(data.newPassword);
	if (passwordError) return passwordError;
	if (data.newPassword !== data.confirmPassword) return "两次输入的密码不一致";
	return null;
};

export const toRegisterSmsPayload = (mobile: string) => ({
	mobile,
});

export const toRegisterPayload = (data: RegisterReq): RegisterPayload => ({
	user_mobile: data.mobile,
	smscode: data.code,
	share: data.share,
	user_password: data.password,
});

export const validateRegisterForm = (data: RegisterForm): string | null => {
	if (!data.share.trim()) return "缺少邀请码";
	if (!data.mobile.trim()) return "请输入手机号码";
	if (!isValidMobile(data.mobile)) return "请输入正确的手机号码";
	if (!data.code.trim()) return "请输入验证码";
	if (!data.password.trim()) return "请输入新密码";
	if (!data.confirmPassword.trim()) return "请确认新密码";
	const passwordError = validatePasswordStrength(data.password);
	if (passwordError) return passwordError;
	if (data.password !== data.confirmPassword) return "两次输入的密码不一致";
	return null;
};
